import isMobile from 'is-mobile';
import { action, computed, observable, runInAction } from 'mobx';
import { UiResponse } from '../CommonInterfaces';
import { PanelType, Panels } from '../panels/PanelTypes';
import { API, FetchStatus } from '../util/ApiUtil';
import CookieApi from '../util/CookieApi';
import ErrorUtil from '../util/ErrorUtil';
import { getStorage } from '../util/StorageUtil';
import { resolveRedirectUrl } from '../util/UiResponseUtil';
import MergeStore from './MergeStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';

export const LAST_PANEL_NAME = 'lastLoginPanelId';
const CSRF_COOKIE_NAME = '_csrfToken';
const MINUTE_IN_MILLIS = 60 * 1000;

export class HomeStore {
  @observable accessor homeResponse: UiResponse = {};
  @observable accessor status: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor isRedirecting: boolean = false;
  @observable accessor defaultPanel: string = '';
  @observable accessor panelsToShow: PanelType[] = [];
  private routeStore: RouteStore;
  private uiStore: UiStore;
  private mergeStore: MergeStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
    this.mergeStore = rootStore.mergeStore;
  }

  public loadIndexData = async () => {
    runInAction(() => {
      this.status = FetchStatus.FETCHING;
      this.homeResponse = {};
    });
    try {
      const response = await API.get<UiResponse>('', { params: this.getIndexParams() });
      this.handleSuccess(response.data);
      this.uiStore.handleNotice(response, false);
      runInAction(() => {
        this.status = FetchStatus.FETCHED;
      });
      this.startTimerForCsrfToken();
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        if (e.response?.data.redirectUrl) {
          this.isRedirecting = true;
          return this.routeStore.route(e.response.data.redirectUrl);
        }
        this.uiStore.handleNotice(e.response, false);
        this.status = FetchStatus.ERROR;
      });
    }
  };

  @action.bound
  public clearRedirectStatus() {
    this.isRedirecting = false;
  }

  @action.bound
  public handleSuccess(data: UiResponse) {
    const allPanels = Panels();
    const redirectUrl = resolveRedirectUrl(data, this.routeStore.formForwardParams);
    if (redirectUrl) {
      this.isRedirecting = true;
      this.routeStore.route(redirectUrl);
    }
    if (!!data && !!data.params) {
      this.mergeStore.mergeParams = data.params;
    }
    this.homeResponse = data;
    this.panelsToShow = data.panels ? allPanels.filter((supportedPanel) => this.allPanelsFilter(supportedPanel, data.panels)) : allPanels;
    this.defaultPanel = this.selectInitialPanel(this.panelsToShow);
  }

  @computed
  public get panels() {
    return this.panelsToShow;
  }

  @computed
  public get initialComponent() {
    return this.homeResponse.component;
  }

  @computed
  public get selectedPanelInStorage() {
    const panelName = getStorage().getItem(LAST_PANEL_NAME);
    return this.isPanelVisible(this.panelsToShow, panelName) ? panelName : '';
  }

  private allPanelsFilter(supportedPanel: PanelType, allowedPanels?: string[]) {
    if (!allowedPanels?.includes(supportedPanel.id)) {
      return false;
    }
    switch (supportedPanel.id) {
      case 'passkey':
        return this.uiStore.isMobileView;
      case 'idcard':
        return this.shouldShowIdCardTab();
      default:
        return true;
    }
  }

  private shouldShowIdCardTab() {
    return !isMobile({ tablet: true, featureDetect: true }) && !this.uiStore.isPotentiallyIpadWebView;
  }

  private selectInitialPanel = (panels: PanelType[]): string => {
    const authLevel = this.routeStore.decodedParams.authlevel;
    let defaultPanel = isMobile() || this.uiStore.isPotentiallyIpadWebView ? 'passkey' : 'smartId';
    if (authLevel === '-1' && this.isPanelVisible(panels, 'admin')) {
      defaultPanel = 'admin';
    }
    const panelName = getStorage().getItem(LAST_PANEL_NAME);
    return (this.isPanelVisible(panels, panelName) ? panelName : this.isPanelVisible(panels, defaultPanel) ? defaultPanel : panels[0].id) as string;
  };

  private isPanelVisible = (panels: PanelType[], panel: string | null) => {
    return panels.some((panelFromLIst) => panelFromLIst.id === panel);
  };

  private getIndexParams() {
    const originalReferrer = document.referrer || window.location.href;
    return { ...this.routeStore.decodedParams, originalReferrer };
  }

  private startTimerForCsrfToken = async () => {
    if (!CookieApi.getCookieValue(CSRF_COOKIE_NAME)) {
      return;
    }
    return setTimeout(() => {
      this.loadIndexData();
    }, MINUTE_IN_MILLIS * 59);
  };
}
